/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import {
  Zap, CheckCircle, ArrowRight, Layers, GitBranch,
  ShieldCheck, Globe, BarChart3, Puzzle, Key, X,
  Bike, UtensilsCrossed, Headphones, LayoutGrid,
} from 'lucide-react';
import { OrgPlan, PLAN_LIMITS } from '../domain/entities';
import { PLAN_LABELS } from '../core/planGating';
import { PLATFORM_MODULES } from '../core/appRegistry';

// ── Pricing data — derived from registry ────────────────────────────────────

const PLANS: {
  plan: OrgPlan;
  name: string;
  price: string;
  period: string;
  highlight: boolean;
  badge?: string;
  cta: string;
  features: string[];
  locked: string[];
}[] = [
  {
    plan: OrgPlan.FREE,
    name: PLAN_LABELS[OrgPlan.FREE],
    price: '$0',
    period: 'forever free',
    highlight: false,
    cta: 'Start for Free',
    features: [
      `${PLAN_LIMITS[OrgPlan.FREE].usageLimits.max_users} Team Members`,
      'Core workflow tools',
      'Community support',
    ],
    locked: ['API Access', 'Webhooks', 'Analytics', 'White-label'],
  },
  {
    plan: OrgPlan.PRO,
    name: PLAN_LABELS[OrgPlan.PRO],
    price: '$49',
    period: 'per org / month',
    highlight: true,
    badge: 'Most Popular',
    cta: 'Start with Pro',
    features: [
      `${PLAN_LIMITS[OrgPlan.PRO].usageLimits.max_users} Team Members`,
      'API Access & Webhooks',
      'Analytics & Reporting',
      'Email support',
    ],
    locked: ['White-label'],
  },
  {
    plan: OrgPlan.ENTERPRISE,
    name: PLAN_LABELS[OrgPlan.ENTERPRISE],
    price: 'Custom',
    period: 'contact us',
    highlight: false,
    cta: 'Contact Sales',
    features: [
      'Unlimited Team Members',
      'Full API & Webhook suite',
      'White-label branding',
      'Dedicated support + SLA',
    ],
    locked: [],
  },
];

// ── Feature comparison rows — pulled live from PLAN_LIMITS ──────────────────

const COMPARISON_PLANS = [OrgPlan.FREE, OrgPlan.BASIC, OrgPlan.PRO, OrgPlan.ENTERPRISE] as const;

interface ComparisonRow {
  label: string;
  values: Record<OrgPlan, string | boolean>;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  {
    label: 'Team Members',
    values: Object.fromEntries(
      COMPARISON_PLANS.map(p => [p, PLAN_LIMITS[p].usageLimits.max_users === Infinity ? 'Unlimited' : String(PLAN_LIMITS[p].usageLimits.max_users)])
    ) as Record<OrgPlan, string>,
  },
  { label: 'API Access',      values: Object.fromEntries(COMPARISON_PLANS.map(p => [p, PLAN_LIMITS[p].apiAccess])) as Record<OrgPlan, boolean> },
  { label: 'Webhooks',        values: Object.fromEntries(COMPARISON_PLANS.map(p => [p, PLAN_LIMITS[p].webhookAccess])) as Record<OrgPlan, boolean> },
  { label: 'Analytics',       values: Object.fromEntries(COMPARISON_PLANS.map(p => [p, PLAN_LIMITS[p].analyticsAccess])) as Record<OrgPlan, boolean> },
  { label: 'System Logs',     values: Object.fromEntries(COMPARISON_PLANS.map(p => [p, PLAN_LIMITS[p].systemLogsAccess])) as Record<OrgPlan, boolean> },
  { label: 'White-label',     values: Object.fromEntries(COMPARISON_PLANS.map(p => [p, PLAN_LIMITS[p].whiteLabelAccess])) as Record<OrgPlan, boolean> },
];

// ── Live module list from registry ──────────────────────────────────────────

const LIVE_MODULES = PLATFORM_MODULES.filter(m => m.id !== 'system-tools' && m.id !== 'test-app');

const FEATURES = [
  {
    icon: Puzzle,
    title: 'Modular App Framework',
    desc: 'Activate only the modules your business needs. Each app handles a specific operational domain — connect them as your workflow grows.',
  },
  {
    icon: GitBranch,
    title: 'Workflow Engine',
    desc: 'Built-in request flows, approval chains, and status tracking across all modules and teams — no custom coding required.',
  },
  {
    icon: Layers,
    title: 'Multi-Tenant Architecture',
    desc: 'Complete data isolation between organisations. One platform, unlimited clients, each with their own workspace and settings.',
  },
  {
    icon: ShieldCheck,
    title: 'Role-Based Access Control',
    desc: 'Platform-level, org-level, and module-level permissions. Everyone sees exactly what they need — nothing more.',
  },
  {
    icon: Key,
    title: 'API & Webhooks',
    desc: 'Integrate ClickIt with your existing stack. Scoped API keys and real-time webhook events keep your systems in sync.',
  },
  {
    icon: BarChart3,
    title: 'Analytics & Reporting',
    desc: 'Cross-module dashboards, detailed activity logs, and performance metrics to guide operational decisions.',
  },
];

const HOW_IT_WORKS = [
  { step: '01', title: 'Set up your organisation', desc: 'Create your workspace, invite your team, and configure your org settings in minutes.' },
  { step: '02', title: 'Activate the modules you need', desc: 'Browse the module library and enable apps that match your operational flow.' },
  { step: '03', title: 'Connect your workflows', desc: 'Link modules together — requests in one app can trigger actions in another.' },
  { step: '04', title: 'Scale at your own pace', desc: 'Upgrade when you need more capacity, more team members, or advanced integrations.' },
];

export function LandingPage({
  onGetStarted,
  onSignIn,
  cms,
}: {
  onGetStarted: (plan?: OrgPlan) => void;
  onSignIn: () => void;
  cms?: {
    platformName?: string;
    logoUrl?: string;
    heroTitle?: string;
    heroDescription?: string;
    heroImageUrl?: string;
    newsItems?: { id: string; title: string; date: string }[];
  };
}) {
  return (
    <div className="min-h-screen bg-white font-sans antialiased text-zinc-900">

      {/* ── Top Bar ─────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {cms?.logoUrl ? (
              <img src={cms.logoUrl} alt={cms?.platformName ?? 'ClickIt'} className="w-8 h-8 rounded-lg object-contain" />
            ) : (
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <Zap size={16} className="text-white" />
              </div>
            )}
            <span className="text-base font-black text-zinc-900 tracking-tight">{cms?.platformName ?? 'ClickIt'}</span>
            <span className="hidden sm:inline text-xs text-zinc-400 font-medium ml-0.5">Platform</span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-zinc-500 font-medium">
            <a href="#features" className="hover:text-zinc-900 transition-colors">Features</a>
            <a href="#modules" className="hover:text-zinc-900 transition-colors">Modules</a>
            <a href="#pricing" className="hover:text-zinc-900 transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            <button onClick={onSignIn} className="px-4 py-2 text-sm font-bold text-zinc-600 hover:text-zinc-900 transition-colors">
              Sign In
            </button>
            <button onClick={() => onGetStarted()} className="px-4 py-2 bg-zinc-900 text-white rounded-xl text-sm font-bold hover:bg-zinc-800 transition-all">
              Get Started Free
            </button>
          </div>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────── */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 pt-24 pb-28 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-500/20 text-indigo-300 rounded-full text-xs font-bold uppercase tracking-widest mb-8">
            <Zap size={11} /> The Modular Operations Platform
          </div>
          {cms?.heroImageUrl && (
            <img src={cms.heroImageUrl} alt="Hero" className="mx-auto mb-8 max-h-64 rounded-2xl object-cover" />
          )}
          <h1 className="text-5xl lg:text-6xl font-black leading-tight tracking-tight mb-6">
            {cms?.heroTitle ?? <>The Modular Operating System<br />for Logistics & Brand Operations</>}
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
            {cms?.heroDescription ?? 'ClickIt is a config-driven platform for managing riders, aggregator campaigns, menus, and support tickets — all under one roof. Start with one module, scale to many.'}
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <button
              onClick={() => onGetStarted()}
              className="flex items-center gap-2 px-7 py-3.5 bg-white text-zinc-900 rounded-2xl text-sm font-black hover:bg-zinc-100 transition-all shadow-lg shadow-white/10"
            >
              Start Free Trial <ArrowRight size={15} />
            </button>
            <button
              onClick={onSignIn}
              className="flex items-center gap-2 px-7 py-3.5 border border-slate-600 text-slate-300 rounded-2xl text-sm font-bold hover:border-slate-400 hover:text-white transition-all"
            >
              Login
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-6">Free forever on Starter plan. No credit card required.</p>
        </div>

        {/* Module Showcase — 4 core modules */}
        <div className="max-w-5xl mx-auto px-6 pb-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Bike,             name: 'Rider Operations',     desc: 'Track riders, transfers, contracts, and branch assignments with full history.' },
              { icon: LayoutGrid,       name: 'Aggregator Campaigns', desc: 'Manage discount campaigns, submissions, and multi-brand approval workflows.' },
              { icon: UtensilsCrossed,  name: 'Menu Management',      desc: 'Build multi-language menus, link modifiers, and sync ministry permits.' },
              { icon: Headphones,       name: 'Support Desk',         desc: 'Issue ticketing, SLA tracking, resolution history, and knowledge base.' },
            ].map(m => (
              <div key={m.name} className="rounded-xl px-5 py-4 bg-white/5 border border-white/10 hover:border-indigo-400/40 transition-all">
                <m.icon size={20} className="text-indigo-400 mb-2.5" />
                <div className="text-sm font-black text-white mb-1">{m.name}</div>
                <div className="text-xs text-slate-400 leading-relaxed">{m.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── News / Announcements (from CMS) ─────────────────────── */}
      {cms?.newsItems && cms.newsItems.length > 0 && (
        <section className="py-14 bg-white border-b border-zinc-100">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-xl font-black text-zinc-900 mb-6">Latest News</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cms.newsItems.map(item => (
                <div key={item.id} className="rounded-xl border border-zinc-200 p-5 hover:shadow-sm transition-shadow">
                  <div className="text-xs text-zinc-400 font-medium mb-1">{item.date}</div>
                  <div className="text-sm font-bold text-zinc-900">{item.title}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── How it works ────────────────────────────────────────── */}
      <section className="py-20 bg-zinc-50" id="modules">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-black text-zinc-900 mb-3">How ClickIt works</h2>
            <p className="text-zinc-500 max-w-xl mx-auto">One platform. Multiple apps. Connected workflows.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_IT_WORKS.map(({ step, title, desc }) => (
              <div key={step} className="relative">
                <div className="text-[11px] font-black text-indigo-500 uppercase tracking-widest mb-3">{step}</div>
                <h3 className="font-black text-zinc-900 mb-2 text-base">{title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────── */}
      <section className="py-20 bg-white" id="features">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-black text-zinc-900 mb-3">Everything built in</h2>
            <p className="text-zinc-500 max-w-xl mx-auto">
              Every module runs on a shared foundation — the same auth, permissions, workflow engine, and APIs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-6 border border-zinc-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center mb-4">
                  <Icon size={20} className="text-indigo-600" />
                </div>
                <h3 className="font-bold text-zinc-900 mb-2">{title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────────────────────── */}
      <section className="py-20 bg-zinc-50" id="pricing">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-black text-zinc-900 mb-3">Simple, transparent pricing</h2>
            <p className="text-zinc-500">Start free. Upgrade when your team needs more.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PLANS.map(plan => {
              const isPro = plan.highlight;
              return (
                <div
                  key={plan.name}
                  className={`relative rounded-2xl p-7 flex flex-col ${
                    isPro
                      ? 'bg-indigo-900 text-white ring-2 ring-indigo-400 shadow-xl shadow-indigo-900/20'
                      : 'bg-white border border-zinc-200 text-zinc-900'
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-3 py-0.5 bg-indigo-400 text-indigo-900 text-[10px] font-black uppercase tracking-widest rounded-full">
                        {plan.badge}
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className={`text-lg font-black mb-1 ${isPro ? 'text-white' : 'text-zinc-900'}`}>
                      {plan.name}
                    </h3>
                    <div className={`text-3xl font-black ${isPro ? 'text-white' : 'text-zinc-900'}`}>
                      {plan.price}
                    </div>
                    <p className={`text-xs mt-1 ${isPro ? 'text-indigo-300' : 'text-zinc-400'}`}>
                      {plan.period}
                    </p>
                  </div>

                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map(f => (
                      <li key={f} className={`flex items-start gap-2 text-sm ${isPro ? 'text-indigo-100' : 'text-zinc-600'}`}>
                        <CheckCircle size={14} className={`mt-0.5 shrink-0 ${isPro ? 'text-indigo-300' : 'text-emerald-500'}`} />
                        {f}
                      </li>
                    ))}
                    {plan.locked.map(f => (
                      <li key={f} className={`flex items-start gap-2 text-sm line-through opacity-40 ${isPro ? 'text-indigo-200' : 'text-zinc-400'}`}>
                        <span className="mt-0.5 shrink-0 w-3.5 h-3.5 flex items-center justify-center text-[10px]">—</span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={plan.plan !== OrgPlan.ENTERPRISE ? () => onGetStarted(plan.plan) : undefined}
                    className={`w-full py-3 rounded-xl text-sm font-black transition-all ${
                      isPro
                        ? 'bg-white text-indigo-900 hover:bg-indigo-50'
                        : 'bg-zinc-900 text-white hover:bg-zinc-800'
                    }`}
                  >
                    {plan.cta}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Feature Comparison Table (live from registry) ────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-black text-zinc-900 mb-3">Compare plans side-by-side</h2>
            <p className="text-zinc-500">All data pulled live from the ClickIt feature registry.</p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-zinc-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50">
                  <th className="text-left px-5 py-4 font-black text-zinc-700">Feature</th>
                  {COMPARISON_PLANS.map(p => (
                    <th key={p} className="px-5 py-4 text-center font-black text-zinc-700">
                      {PLAN_LABELS[p]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, i) => (
                  <tr key={row.label} className={i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/50'}>
                    <td className="px-5 py-3 font-medium text-zinc-700">{row.label}</td>
                    {COMPARISON_PLANS.map(p => {
                      const v = row.values[p];
                      return (
                        <td key={p} className="px-5 py-3 text-center">
                          {typeof v === 'boolean'
                            ? v
                              ? <CheckCircle size={16} className="text-emerald-500 inline-block" />
                              : <X size={16} className="text-zinc-300 inline-block" />
                            : <span className="font-bold text-zinc-900">{v}</span>
                          }
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── Footer CTA ──────────────────────────────────────────── */}
      <section className="py-20 bg-slate-900 text-white text-center">
        <div className="max-w-2xl mx-auto px-6">
          <Zap size={32} className="text-indigo-400 mx-auto mb-4" />
          <h2 className="text-3xl font-black mb-4">Ready to build your stack?</h2>
          <p className="text-slate-300 mb-8">
            Join teams already using ClickIt to manage their operational flows.
            Start free — no credit card, no commitment.
          </p>
          <button
            onClick={() => onGetStarted()}
            className="inline-flex items-center gap-2 px-8 py-4 bg-indigo-600 text-white rounded-2xl font-black text-sm hover:bg-indigo-500 transition-all shadow-lg shadow-indigo-900/40"
          >
            Create Your Free Account <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <footer className="py-6 border-t border-zinc-100 bg-white">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-xs text-zinc-400">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 bg-indigo-600 rounded-md flex items-center justify-center">
              <Zap size={10} className="text-white" />
            </div>
            <span>© {new Date().getFullYear()} ClickIt Platform</span>
          </div>
          <button onClick={onSignIn} className="hover:text-zinc-700 transition-colors">Sign In →</button>
        </div>
      </footer>
    </div>
  );
}
